import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Play, Clock, Film, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/config';

const CATEGORY_KEYS = ['all', 'nasca', 'palpa', 'museum', 'archaeological', 'nature', 'other'];
const LANGUAGE_META = [
  { value: 'all', flag: '🌐' },
  { value: 'es', flag: '🇪🇸' },
  { value: 'en', flag: '🇺🇸' },
  { value: 'ja', flag: '🇯🇵' },
  { value: 'pt', flag: '🇵🇹' },
  { value: 'zh', flag: '🇨🇳' },
  { value: 'it', flag: '🇮🇹' },
  { value: 'other', flag: '' }
];

const DemosPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');

  const handleVideoClick = (video) => {
    navigate(`/video/${video.id}`);
  };

  useEffect(() => {
    fetchDemos();
  }, [selectedCategory, selectedLanguage]);

  const fetchDemos = async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      if (selectedCategory !== 'all') qs.set('category', selectedCategory);
      if (selectedLanguage !== 'all') qs.set('language', selectedLanguage);
      const url = `${API}/videos/demos${qs.toString() ? `?${qs.toString()}` : ''}`;
      const response = await axios.get(url);
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching demos:', error);
      toast.error('Error al cargar los demos');
    } finally {
      setLoading(false);
    }
  };

  const resolveImg = (src) => {
    if (!src) return null;
    return src.startsWith('/api') ? `${BACKEND_URL}${src}` : src;
  };

  const langLabel = (v) => {
    const b = { es: '🇪🇸 ES', en: '🇺🇸 EN', ja: '🇯🇵 JA', pt: '🇵🇹 PT', zh: '🇨🇳 ZH', it: '🇮🇹 IT' };
    return b[v] || v?.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50" data-testid="demos-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {t('demos.badge')}
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {t('demos.title')}
            </h1>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              {t('demos.subtitle')}
            </p>
            <div className="flex items-center justify-center gap-2 mt-6">
              <div className="h-px w-20 bg-gradient-to-r from-transparent to-amber-400"></div>
              <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
              <div className="h-px w-20 bg-gradient-to-l from-transparent to-amber-400"></div>
            </div>
          </div>

          {/* Category Filters */}
          <div data-testid="demo-category-filters" className="flex flex-wrap justify-center gap-3 mb-4">
            {CATEGORY_KEYS.map((catKey) => (
              <Button
                key={catKey}
                data-testid={`demo-category-${catKey}`}
                onClick={() => setSelectedCategory(catKey)}
                className={`px-5 py-2 rounded-full font-medium ${
                  selectedCategory === catKey
                    ? 'bg-gradient-to-r from-amber-600 to-orange-700 text-white'
                    : 'bg-white text-gray-700 border-2 border-amber-300 hover:border-amber-500'
                }`}
              >
                {t(`categories.${catKey}`)}
              </Button>
            ))}
          </div>

          {/* Language Filters */}
          <div data-testid="demo-language-filters" className="flex flex-wrap justify-center gap-2 mb-10">
            {LANGUAGE_META.map((lang) => (
              <button
                key={lang.value}
                data-testid={`demo-language-${lang.value}`}
                onClick={() => setSelectedLanguage(lang.value)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedLanguage === lang.value
                    ? 'bg-amber-900 text-white'
                    : 'bg-white text-amber-800 border border-amber-200 hover:bg-amber-50'
                }`}
              >
                {lang.flag && <span className="mr-1">{lang.flag}</span>}
                {lang.value === 'all' ? t('gallery.allLanguages') : t(`languages.${lang.value}`)}
              </button>
            ))}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-16 bg-white/60 backdrop-blur rounded-3xl border-2 border-amber-200" data-testid="no-demos">
              <Film className="w-12 h-12 mx-auto text-amber-400 mb-3" />
              <p className="text-amber-900 font-medium">{t('demos.noDemos')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="demos-grid">
              {videos.map((video) => (
                <div
                  key={video.id}
                  data-testid={`demo-card-${video.id}`}
                  onClick={() => handleVideoClick(video)}
                  className="group bg-white rounded-2xl overflow-hidden border-2 border-amber-100 hover:border-amber-300 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  {/* Thumbnail */}
                  <div className="relative h-52 overflow-hidden bg-gradient-to-br from-amber-100 to-orange-100">
                    {video.thumbnail_url ? (
                      <img
                        src={resolveImg(video.thumbnail_url)}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="w-12 h-12 text-amber-400" />
                      </div>
                    )}
                    {/* Play overlay */}
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-amber-700 ml-1" fill="currentColor" />
                      </div>
                    </div>
                    {/* Duration */}
                    {video.duration && (
                      <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-black/70 backdrop-blur text-white text-xs px-2.5 py-1 rounded-full">
                        <Clock size={11} />
                        {video.duration}
                      </div>
                    )}
                    {/* Free badge */}
                    <div className="absolute top-3 right-3 bg-green-600 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-md">
                      DEMO
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-lg font-bold text-amber-900 group-hover:text-amber-700 transition-colors line-clamp-2">
                        {video.title}
                      </h3>
                      {video.language && video.language !== 'other' && (
                        <span className="shrink-0 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold uppercase">
                          {langLabel(video.language)}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm line-clamp-3">
                      {video.description}
                    </p>
                    <div className="mt-3 text-xs text-amber-700/70">
                      {t(`categories.${video.category}`, { defaultValue: video.category })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemosPage;
